import { Suspense, useState, Dispatch, SetStateAction } from "react";
import styled from "styled-components/macro";
import { BORDER_RADIUS } from "../vars";
import ContactAvatar from "./ContactAvatar";
import Button from "./Button";
import ContactSelector from "./ContactSelector";
import { Plus } from "./Icons";
import useScrollTriggers from "../hooks/use-scroll-triggers";
import Stack from "./Stack";
import { NewContactSidePanel } from "./ContactEditor";
import { useSkipioQueries } from "../hooks/use-query";

const PAGE_SIZE = 6;

type ValueTemplate = {
  contactIds: string[];
  groupIds: string[];
  keywordIds: string[];
};

function ContactsWell<Value extends ValueTemplate>({
  value,
  onChange,
  canAdd = true,
  canRemove = true,
  allowGroups = false,
  placeholder = "No Contacts Selected",
}: {
  value: Value;
  onChange: Dispatch<SetStateAction<Value>>;
  canAdd?: boolean;
  canRemove?: boolean;
  allowGroups?: boolean;
  placeholder?: string;
}) {
  const { contactIds, groupIds = [], keywordIds = [] } = value;
  const [renderLimit, setRenderLimit] = useState(PAGE_SIZE);

  const groupContactCounts = useSkipioQueries({
    queries: groupIds.map((id) => {
      return {
        queryKey: ["groups", id],
        suspense: false,
        useErrorBoundary: true,
        enabled: !!groupIds,
      };
    }),
  }).map((query) => query.isSuccess && query.data.contactIds.length);

  const keywordSubscriberCounts = useSkipioQueries({
    queries: keywordIds.map((id) => {
      return {
        queryKey: ["keywords", id],
        suspense: false,
        useErrorBoundary: true,
        enabled: !!keywordIds,
      };
    }),
  }).map((query) => query.isSuccess && query.data.activeSubscriberCount);

  const total = [
    ...(contactIds ? contactIds.map(() => 1) : []),
    ...groupContactCounts,
    ...keywordSubscriberCounts,
  ].reduce((a, b) => a + b, 0);

  const ref = useScrollTriggers(({ fromBottom }) => {
    if (fromBottom < 50 && renderLimit < contactIds.length + groupIds.length) {
      setRenderLimit((limit) => limit + PAGE_SIZE);
    }
  });

  return (
    <Container>
      <ContactList ref={ref}>
        <div
          hidden={contactIds.length + groupIds.length + keywordIds.length !== 0}
        >
          {
            <>
              {canAdd ? (
                <ContactSelector
                  value={value}
                  onChange={(patch) => {
                    onChange((value: any) => {
                      const newValue =
                        typeof patch === "function"
                          ? { ...value, ...patch(value) }
                          : { ...value, ...patch };
                      return newValue;
                    });
                  }}
                  allowGroups={allowGroups}
                  trigger={({ ref, show }) => (
                    <Button variant="link" ref={ref} onClick={show}>
                      <div>
                        <Stack space={1}>
                          <Plus style={{ width: "24px", height: "24px" }} />
                          Add Contacts
                        </Stack>
                      </div>
                    </Button>
                  )}
                />
              ) : (
                <span>{placeholder}</span>
              )}
            </>
          }
        </div>
        {groupIds.map((groupId) => (
          <Suspense key={groupId} fallback={<ContactAvatar />}>
            <ContactAvatar
              groupId={groupId}
              onRemove={
                canRemove
                  ? () =>
                      onChange((value) => {
                        return {
                          ...value,
                          groupIds: groupIds.filter((id) => id !== groupId),
                        };
                      })
                  : undefined
              }
            />
          </Suspense>
        ))}
        {keywordIds.map((keywordId) => (
          <Suspense key={keywordId} fallback={<ContactAvatar />}>
            <ContactAvatar
              keywordId={keywordId}
              onRemove={
                canRemove
                  ? () =>
                      onChange((value) => {
                        return {
                          ...value,
                          keywordIds: keywordIds.filter(
                            (id) => id !== keywordId
                          ),
                        };
                      })
                  : undefined
              }
            />
          </Suspense>
        ))}
        {contactIds.slice(0, renderLimit).map((contactId) => (
          <Suspense key={contactId} fallback={<ContactAvatar />}>
            <ContactAvatar
              contactId={contactId}
              onRemove={
                canRemove
                  ? () =>
                      onChange((value) => {
                        return {
                          ...value,
                          contactIds: contactIds.filter(
                            (id) => id !== contactId
                          ),
                        };
                      })
                  : undefined
              }
            />
          </Suspense>
        ))}
      </ContactList>
      <Toolbar>
        <div>
          <span>
            {contactIds.length && groupIds.length ? (
              <>
                <var data-pluralize={contactIds.length} data-count="true">
                  {contactIds.length}
                </var>{" "}
                contact{contactIds.length !== 1 ? "s" : ""} and{" "}
                <var data-pluralize={groupIds.length} data-count="true">
                  {groupIds.length}
                </var>{" "}
                group{groupIds.length !== 1 ? "s" : ""} selected
              </>
            ) : contactIds.length && keywordIds.length ? (
              <>
                <var data-pluralize={contactIds.length} data-count="true">
                  {contactIds.length}
                </var>{" "}
                contact{contactIds.length !== 1 ? "s" : ""} and{" "}
                <var data-pluralize={keywordIds.length} data-count="true">
                  {keywordIds.length}
                </var>{" "}
                keyword group{keywordIds.length !== 1 ? "s" : ""} selected
              </>
            ) : contactIds.length > 0 ? (
              <>
                <var data-pluralize={contactIds.length} data-count="true">
                  {contactIds.length}
                </var>{" "}
                contact{contactIds.length !== 1 ? "s" : ""}{" "}
              </>
            ) : groupIds.length > 0 ? (
              <>
                <var data-pluralize={groupIds.length} data-count="true">
                  {groupIds.length}
                </var>{" "}
                group{groupIds.length !== 1 ? "s" : ""} selected
              </>
            ) : keywordIds.length > 0 ? (
              <>
                <var data-pluralize={keywordIds.length} data-count="true">
                  {keywordIds.length}
                </var>{" "}
                keyword group{keywordIds.length !== 1 ? "s" : ""} selected
              </>
            ) : (
              "0 contacts"
            )}{" "}
            {Boolean(
              total > 0 && (groupIds.length > 0 || keywordIds.length > 0)
            ) && <>({total} total contacts)</>}
          </span>
          {canAdd && (
            <ContactSelector
              value={value}
              onChange={(patch) => {
                onChange((value) => {
                  const newValue =
                    typeof patch === "function"
                      ? { ...value, ...patch(value) }
                      : { ...value, ...patch };
                  return newValue;
                });
              }}
              allowGroups={allowGroups}
              trigger={({ ref, show }) => (
                <Button variant="link" ref={ref} onClick={show}>
                  Edit
                </Button>
              )}
            />
          )}
        </div>
        {canAdd && (
          <div>
            {/* @ts-expect-error */}
            <NewContactSidePanel
              onSave={(contact: { id: string }) => {
                onChange((value) => {
                  return {
                    ...value,
                    contactIds: [...(value.contactIds || []), contact.id],
                  };
                });
              }}
              //@ts-expect-error
              trigger={({ show, ref }) => (
                <Button variant="link" ref={ref} onClick={show}>
                  New Contact
                </Button>
              )}
            />
          </div>
        )}
      </Toolbar>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${BORDER_RADIUS};
  border: 0.5px solid #dcdcdc;
  overflow: hidden;
  height: 150px;
  flex-shrink: 0;
`;

const ContactList = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fafafa;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.02) inset;
  overflow-x: hidden;
  overflow-y: auto;
  flex-grow: 1;
  flex-wrap: wrap;
`;

const Toolbar = styled.div`
  height: 36px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5px solid #dcdcdc;
  padding: 0 20px;
  padding-right: 0;
  font-size: 12px;
  flex-shrink: 0;
`;

export default ContactsWell;
